import { useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { usePermissions, Permission } from "@/hooks/use-permissions";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useOrganizationSelector } from "@/hooks/use-organization-selector";
import { Button } from "@/components/ui/button";
import { useTranslation } from 'react-i18next';
import { trackEvent } from '@/lib/analytics';
import { ANALYTICS_EVENTS } from '@mangelapp/shared';

// SVG Icon Component for sidebar icons
interface SidebarIconProps {
  src: string;
  alt: string;
  className?: string;
}

const iconSizeClass = "h-[30px] w-[30px]";

function SidebarIcon({ src, alt, className }: SidebarIconProps) {
  return (
    <img
      src={src}
      alt={alt}
      className={cn("flex-shrink-0 sidebar-icon-stroke", className)}
      aria-hidden="true"
    />
  );
}

function getSelectedIconSrc(src: string, isSelected: boolean): string {
  if (!isSelected) return src;
  return src.replace(/(\.[a-zA-Z0-9]+)$/, "_selected$1");
}

const navigation = [
  { id: 'dashboard', href: "/dashboard", iconSrc: "/images/icons/web_icon_dashboard.svg", iconComponent: null, permission: null },
  { id: 'projects', href: "/projects", iconSrc: "/images/icons/web_icon_projects.svg", iconComponent: null, permission: ['manage_projects', 'view_projects'] as Permission[] },
  { id: 'calendar', href: "/calendar", iconSrc: "/images/icons/web_icon_calendar.svg", iconComponent: null, permission: ['manage_projects', 'manage_all_projects'] as Permission[] },
  { id: 'lists', href: "/listen", iconSrc: "/images/icons/web_icon_organization.svg", iconComponent: null, permission: 'manage_lists' as Permission },
  { id: 'contractors', href: "/contractors", iconSrc: "/images/icons/web_icon_team.svg", iconComponent: null, permission: 'manage_members' as Permission },
] as const;

const bottomNavigation = [
  { id: 'settings', href: "/settings", iconSrc: "/images/icons/web_icon_settings.svg", iconComponent: null },
] as const;

export default function Sidebar() {
  const { t } = useTranslation(['components.sidebar', 'common']);
  const [location] = useLocation();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const userName = localStorage.getItem('userName') || t('components.sidebar:user');

  // Check permissions using the new organization-based system
  const { hasPermission, hasAnyPermission, userRole, permissionsLoading } = usePermissions();
  const { activeOrganizationId } = useOrganizationSelector();

  const { data: projectsAccessResponse } = useQuery({
    queryKey: ["/api/projects", "sidebar-access", activeOrganizationId],
    queryFn: async () => {
      const url = activeOrganizationId
        ? `/api/projects?organizationId=${encodeURIComponent(activeOrganizationId)}&light=1`
        : "/api/projects?light=1";
      const response = await apiRequest(url);
      return await response.json();
    },
    enabled: !!activeOrganizationId,
    staleTime: 60_000,
    retry: false,
  });
  const hasAssignedProjects = useMemo(() => {
    const rows = (projectsAccessResponse as any)?.data;
    const meta = (projectsAccessResponse as any)?.meta;
    const hasRows = Array.isArray(rows) && rows.length > 0;
    const hiddenBySubscription = meta?.projectsHiddenDueToSubscription === true;
    return hasRows || hiddenBySubscription;
  }, [projectsAccessResponse]);

  // Current user
  const { user: currentUser } = useCurrentUser();
  // Check if user is contractor in the SELECTED organization
  const { isContractor, activeOrgRole } = useOrganizationSelector();
  const isRestrictedRole = activeOrgRole === 'watcher' || activeOrgRole === 'business_owner';

  // Helper function to check if user has permission for a route
  const hasRoutePermission = (permission: Permission | Permission[] | null, href?: string): boolean => {
    const isRestrictedRole = userRole === 'watcher' || userRole === 'business_owner';
    if (isRestrictedRole) {
      const allowedRoutes = new Set(['/projects', '/settings']);
      if (href && !allowedRoutes.has(href)) {
        return false;
      }
    }
    if (permission === null) return true; // No permission required
    if (href === '/projects' && (permissionsLoading || hasAssignedProjects)) {
      return true;
    }
    if (Array.isArray(permission)) {
      return hasAnyPermission(permission);
    }
    return hasPermission(permission);
  };

  // Helper function to get permission display name
  const getPermissionDisplayName = (permission: Permission | Permission[] | null): string => {
    if (permission === null) return '';
    if (Array.isArray(permission)) {
      return permission.join(', ');
    }
    return permission;
  };

  // Check for unread support messages (for non-admin users)
  const { data: supportMessagesResponse = [] } = useQuery({
    queryKey: ["/api/support/messages"],
    queryFn: async () => {
      const response = await apiRequest("/api/support/messages");
      return await response.json();
    },
    enabled: (currentUser as any)?.role !== "admin" && (currentUser as any)?.role !== "super_admin",
    staleTime: 2 * 60 * 1000, // 2 minutes - messages can change
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const supportMessages = supportMessagesResponse?.data || [];

  const isAdmin = (currentUser as any)?.role === "admin" || (currentUser as any)?.role === "super_admin";

  // Count unread admin messages from ALL folders
  const unreadCount = (supportMessages as any[]).filter((msg: any) =>
    !msg.isFromUser && !msg.isRead
  ).length;

  return (
    <div
      className="bg-transparent text-white flex-shrink-0 flex flex-col overflow-visible w-[3.25rem] sm:w-[3.75rem] md:w-[4.25rem] lg:w-[3.75rem] xl:w-[4.25rem] 2xl:w-[5.25rem] fixed left-0 top-0 bottom-0 z-40"
    >
      {/* Logo/Brand - Aligns with Row 1 - Fixed height to match header Row 1 exactly */}
      <div className="px-3 flex-shrink-0 h-[80px] flex items-center justify-center">
        <Link
          href="/dashboard"
          className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center flex-shrink-0 cursor-pointer"
          onClick={() => {
            // Navigate to dashboard and refresh the page
            window.location.href = '/dashboard';
          }}
        >
          <img
            src="/images/logos/corner_logo.png"
            alt="FOLW Logo"
            className="w-full h-full object-contain"
          />
        </Link>
      </div>

      {/* Spacer for Row 2 - Empty row to align with header Row 2 */}
      <div className="h-[35px] flex-shrink-0"></div>

      {/* Spacer for Row 3 - Empty row to align with header Row 3 */}
      <div className="h-[50px] flex-shrink-0"></div>

      {/* Main Navigation - Starts after Row 3, keeps original vertical layout */}
      <nav className="px-1.5 py-0 overflow-visible flex-1">
        {(isRestrictedRole ? navigation.filter((item) => item.id === 'projects') : navigation).map((item) => {
          const hasAccess = hasRoutePermission(item.permission, item.href);
          const isActive = location === item.href || location.startsWith(item.href);
          const IconComponent = item.iconComponent;
          const isDisabled = !hasAccess;
          const iconSrc = getSelectedIconSrc(item.iconSrc, isActive && !isDisabled);
          const permissionName = getPermissionDisplayName(item.permission);

          const navItem = (
            <div
              className={cn(
                "flex items-center justify-center px-2 py-1.5 rounded relative",
                !isDisabled
                  ? "cursor-pointer"
                  : "cursor-not-allowed opacity-50",
                isActive && !isDisabled
                  ? "text-[#333333]"
                  : !isDisabled
                    ? "text-[#333333] hover:bg-gray-100"
                    : "text-gray-400"
              )}
              onClick={(e) => {
                if (isDisabled) {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
            >
              {iconSrc ? (
                <SidebarIcon
                  src={iconSrc}
                  alt={t(`components.sidebar:nav.${item.id}`)}
                  className={iconSizeClass}
                />
              ) : IconComponent ? (
                <IconComponent className={`${iconSizeClass} flex-shrink-0`} />
              ) : null}

              {isDisabled && permissionName && (
                <span className="sr-only">
                  {t('common:accessDenied.requiredPermission', { permission: permissionName })}
                </span>
              )}
            </div>
          );

          return (
            <div key={item.id} className="mb-2 relative overflow-visible">
              {!isDisabled ? (
                <Link 
                  href={item.href}
                  onClick={() => {
                    // Dispatch event to reset dashboard tab when clicking dashboard while already on dashboard
                    if (item.id === 'dashboard' && location === '/dashboard') {
                      window.dispatchEvent(new CustomEvent('dashboard-reset'));
                    }
                  }}
                >
                  {navItem}
                </Link>
              ) : (
                navItem
              )}
            </div>
          );
        })}
        <div className="h-9 mb-2" />
        {bottomNavigation.map((item) => {
          // Keine spezielle Filterung mehr nötig

          const isActive = location === item.href;
          const IconComponent = item.iconComponent;
          const iconSrc = getSelectedIconSrc(item.iconSrc, isActive);
          const isSettings = item.id === 'settings';

          return (
            <div key={item.id} className="mb-2 relative overflow-visible">
              <Link href={item.href}>
                <div
                  className={cn(
                    "flex items-center justify-center px-2 py-1.5 rounded cursor-pointer relative",
                    isActive
                      ? "text-[#333333]"
                      : "text-[#333333] hover:bg-gray-100"
                  )}
                >
                  {iconSrc ? (
                    <SidebarIcon
                      src={iconSrc}
                      alt={t(`nav.${item.id}`)}
                      className={iconSizeClass}
                    />
                  ) : IconComponent ? (
                    <IconComponent className={`${iconSizeClass} flex-shrink-0`} />
                  ) : null}

                  {/* Notification Badge für Einstellungen */}
                  {isSettings && unreadCount > 0 && (
                    <div className="absolute -top-1 -right-0.5 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-bold leading-none" style={{ fontSize: '9px' }}>
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    </div>
                  )}

                  {isSettings && unreadCount > 0 && (
                    <span className="sr-only">{t('tooltips.settingsUnread', { count: unreadCount })}</span>
                  )}
                </div>
              </Link>
            </div>
          );
        })}

        {/* Logout Button */}
        <div className="mt-2 relative overflow-visible">
          <div
            className="flex items-center justify-center px-2 py-1.5 rounded cursor-pointer relative text-[#333333] hover:text-red-500 hover:bg-red-50 transition-colors"
            onClick={() => setShowLogoutDialog(true)}
          >
            <SidebarIcon
              src="/images/icons/web_icon_logout.svg"
              alt={t('tooltips.logout')}
              className={iconSizeClass}
            />
          </div>
        </div>
      </nav>

      {/* Logout Confirmation Dialog */}
      <Dialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-900">
              {t('logoutDialog.title')}
            </DialogTitle>
            <DialogDescription className="mt-2 text-sm text-gray-600">
              {t('logoutDialog.description', { name: userName })}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-6 flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowLogoutDialog(false)}
              className="h-8 px-4 text-sm font-medium"
            >
              {t('logoutDialog.cancel')}
            </Button>
            <Button
              onClick={() => {
                trackEvent(ANALYTICS_EVENTS.LOGOUT);
                // Clear authentication data
                localStorage.removeItem('sessionToken');
                localStorage.removeItem('userCode');
                localStorage.removeItem('userId');
                localStorage.removeItem('userName');
                window.location.href = '/login';
              }}
              className="h-8 px-4 text-sm font-medium bg-red-600 hover:bg-red-700"
            >
              {t('logoutDialog.confirm')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
